// @flow
import { get, pick, isEmpty } from 'lodash';

import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { graphqlify } from 'Utils/graphql';
import {
  getPaymentScheduleRequest,
  getPriceDetailFragment,
  proposalPriceQuery,
} from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import { PROPOSAL_UPDATE_ATTENDEES } from 'Utils/request/requestDictionary';
import { timestampToApiDate } from 'Utils/date/apiDate';
import { isSellerAndInMultiplesFamiliesContext } from 'Utils/seller/sellerUtils';

export type updateAttendeesMutationType = {
  proposalId: string,
  attendees: Participants,
  appContext: AppContext,
};

const ATTENDEE_SCHEMA_PROPERTIES = [
  'id',
  'gmNumber',
  'lastName',
  'firstName',
  'unicodeLastName',
  'unicodeFirstName',
  'birthdate',
  'email',
  'mobile',
  'optinSms',
  'optinMail',
  'optinFosun',
  'optinEmail',
  'optinPhone',
  'optinClubmedPlus',
  'civility',
  'identityNumber',
  'passport',
  'customerId',
  'address',
  'mainContactId',
  'isChild',
];

/**
 * Remove all properties that don't match GQL Schema Otherwise the mutation fails
 */
const removeUselessProperties = attendee => {
  return pick(attendee, ATTENDEE_SCHEMA_PROPERTIES);
};

/**
 * Connected customer experience : Put attendees
 */
function updateAttendeesMutation({
  proposalId,
  attendees,
  appContext,
}: updateAttendeesMutationType): Promise<*> {
  const formatedAttendees = attendees.map(attendee =>
    removeUselessProperties({
      ...attendee,
      customerId: attendee.neolid,
      birthdate: timestampToApiDate(attendee.birthdate),
      passport: !isEmpty(attendee.passport)
        ? {
            ...attendee.passport,
            expirationDate: timestampToApiDate(attendee.passport.expirationDate),
            issuingDate: timestampToApiDate(attendee.passport.issuingDate),
          }
        : undefined,
    }),
  );

  return graphQLClient.runNoCacheQuery(
    `
    mutation {
      ${
        isSellerAndInMultiplesFamiliesContext(appContext, attendees)
          ? 'modifyAttendeesSellerMultipleFamilies'
          : 'modifyAttendees'
      } (
      proposalId: "${proposalId}",
      attendees: ${graphqlify(formatedAttendees)}
     ) {
        proposal {
          id
          attendees {
            list {
              id
              customerId
            }
          }
          ${proposalPriceQuery()}
          ${getPaymentScheduleRequest(appContext)}
        }
        ${appContext.isDirectCxo || appContext.isIndirectCxo ? '' : 'customerStatus'}
        errors {
          errorKey
          errorValue
          errorCode
        }
        ${
          appContext.isDirectCxo || appContext.isIndirectCxo
            ? `attendees {
                id
                gmNumber
                customerId
              }`
            : ''
        }
      }
    }
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    `,
    appContext,
    { requestName: PROPOSAL_UPDATE_ATTENDEES },
  );
}

const queryBookingAttendees = (bookingId: string) => {
  return graphQLClient.runQuery(`query {
    bookingAttendees(bookingId: ${bookingId}) {
      civility
      gmNumber
      customerId
      identityNumber
      mainContact
      lastName
      unicodeLastName
      firstName
      unicodeFirstName
      email
      birthdate
    }
  }`);
};

export { updateAttendeesMutation, queryBookingAttendees };
